import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { notes } from '../data/notes';
import RowVsColumnDemo from '../components/RowVsColumnDemo';

const noteContent: Record<string, React.ReactNode> = {
  'what-is-clickhouse': (
    <>
      <p>ClickHouse is an open-source, high-performance, column-oriented SQL database management system (DBMS) purpose-built for Online Analytical Processing (OLAP) and real-time data analytics.</p>
      <p>Originally developed by the Russian tech company Yandex in 2016, it lets you run complex analytical reports across billions of rows in milliseconds.</p>

      <h2>The Short Version</h2>
      <ul>
        <li><strong>Column-oriented</strong> — data is stored column by column instead of row by row. (Why that matters is the whole next note.)</li>
        <li><strong>Built for OLAP</strong> — optimized for "compute something across millions of rows" queries, not "fetch me one record" queries.</li>
        <li><strong>Fast at scale</strong> — designed to scan billions of rows and return an aggregate result in milliseconds.</li>
      </ul>

      <p>That's the whole definition. Everything else — why it's fast, how it physically stores data, and where it fits in an observability stack — is what the next two notes build on.</p>
    </>
  ),

  'why-clickhouse-for-observability': (
    <>
      <p>Observability tools store huge volumes of <em>spans</em> — a span is one timed operation in a trace (the next note covers this in full). The real question is: what makes ClickHouse the storage engine almost every observability vendor reaches for?</p>

      <h2>Understand by Example</h2>
      <p>Imagine a tiny table of 3 spans, with 3 columns: <code>service</code>, <code>duration</code>, <code>status</code>.</p>

      <table>
        <thead>
          <tr><th>service</th><th>duration</th><th>status</th></tr>
        </thead>
        <tbody>
          <tr><td>checkout</td><td>120ms</td><td>ok</td></tr>
          <tr><td>checkout</td><td>340ms</td><td>error</td></tr>
          <tr><td>payments</td><td>95ms</td><td>ok</td></tr>
        </tbody>
      </table>

      <p>A normal database (Postgres, MySQL) physically stores this <strong>row by row</strong> on disk — like three index cards, each card holding one full span:</p>
      <pre><code>{`[checkout, 120ms, ok]  [checkout, 340ms, error]  [payments, 95ms, ok]`}</code></pre>
      <p>That's great if your question is "give me everything about this one span." But real observability questions are almost never that — they're things like <em>"what's the average duration for checkout over the last hour?"</em> To answer that, a row store has to open every card and pull out just the <code>duration</code> field, throwing the rest away. With millions of spans, that's a lot of wasted reading.</p>

      <p>ClickHouse instead stores the same data <strong>column by column</strong> — one long strip per field:</p>
      <pre><code>{`service:  [checkout, checkout, payments]
duration: [120ms, 340ms, 95ms]
status:   [ok, error, ok]`}</code></pre>
      <p>Now "average duration" means: read the <code>duration</code> strip, ignore everything else. You never even touch <code>service</code> or <code>status</code>. That's the entire trick — every other ClickHouse feature (compression, <code>PARTITION BY</code>, <code>ORDER BY</code>) is a refinement on top of it, because storing a column together also means it compresses extremely well (a strip of 10 million <code>status</code> values that are almost all <code>"ok"</code> shrinks to almost nothing), and you can skip entire chunks of the strip once partition metadata says they're outside your time range.</p>

      <RowVsColumnDemo />

      <p style={{ color: 'var(--text2)', fontStyle: 'italic', marginTop: 8, padding: '20px 24px', background: 'var(--surface)', borderRadius: 12, borderLeft: '3px solid var(--cyan)' }}>
        The whole mental model in one line: a row store is optimized for "give me one record." A column store is optimized for "compute something across millions of records." Observability is 95% the second kind of question — which is exactly why SigNoz, Datadog, and Honeycomb all build on a column store.
      </p>
    </>
  ),

  'what-is-a-span': (
    <>
      <p>A span is a single, timed unit of work or operation within a distributed application trace — the basic record that observability systems like ClickHouse-backed SigNoz store and query at scale.</p>

      <h2>What a Span Represents</h2>
      <ul>
        <li><strong>A single operation</strong> — an inbound HTTP request, a database query, or a function call.</li>
        <li><strong>Core metadata</strong> — a start time, duration, trace ID, span ID, parent ID, and custom attributes (tags).</li>
        <li><strong>Building blocks</strong> — connected groups of spans form a complete tree called a trace, mapping an end-to-end transaction across microservices.</li>
      </ul>

      <h2>Why Spans Matter in ClickHouse</h2>
      <ul>
        <li><strong>Row mapping</strong> — when storing observability data, each span is typically saved as an individual row in a ClickHouse table.</li>
        <li><strong>Columnar efficiency</strong> — ClickHouse stores each column separately rather than grouping entire rows together, which allows fast filtering and high-ratio compression across billions of spans.</li>
        <li><strong>Fast analytics</strong> — engineers use ClickHouse SQL to measure duration, find bottlenecks, and trace system performance across massive volumes of telemetry data.</li>
      </ul>

      <p style={{ color: 'var(--text2)', fontStyle: 'italic', marginTop: 8, padding: '20px 24px', background: 'var(--surface)', borderRadius: 12, borderLeft: '3px solid var(--cyan)' }}>
        Put together: a span is one row. A trace is a tree of related rows. And ClickHouse's column-oriented storage — see the previous note — is what makes querying billions of these rows fast enough for a live dashboard.
      </p>
    </>
  ),
};

export default function NoteDetail() {
  const { slug } = useParams<{ slug: string }>();
  const note = notes.find(n => n.slug === slug);

  if (!note) {
    return (
      <div style={{ paddingTop: 120, textAlign: 'center', padding: '120px 24px' }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 48, marginBottom: 16 }}>404</h1>
        <p style={{ color: 'var(--text2)', marginBottom: 32 }}>Note not found.</p>
        <Link to="/notes" style={{ color: 'var(--cyan)' }}>← Back to Notes</Link>
      </div>
    );
  }

  const content = noteContent[note.slug];
  const seriesNotes = notes.filter(n => n.series === note.series).sort((a, b) => a.order - b.order);
  const otherInSeries = seriesNotes.filter(n => n.slug !== note.slug);

  return (
    <div style={{ paddingTop: 64 }}>
      <div style={{ padding: '32px 24px 0', maxWidth: 780, margin: '0 auto' }}>
        <Link to="/notes" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text2)', fontSize: 13, marginBottom: 40 }}>
          <ArrowLeft size={14} /> Back to Notes
        </Link>
      </div>

      <header style={{ padding: '0 24px 40px', maxWidth: 780, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: note.color, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{note.series}</span>
          <span style={{ color: 'var(--text3)' }}>·</span>
          <span style={{ color: 'var(--text3)', fontSize: 12, fontFamily: 'var(--font-mono)' }}>Part {note.order} of {seriesNotes.length}</span>
        </div>
        <h1 style={{
          fontFamily: 'var(--font-display)', fontSize: 'clamp(28px, 4vw, 48px)',
          fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.025em', marginBottom: 20,
        }}>{note.title}</h1>
        <p style={{ color: 'var(--text2)', fontSize: 18, lineHeight: 1.7, paddingBottom: 32, borderBottom: '1px solid var(--border)' }}>{note.summary}</p>
      </header>

      <article style={{ padding: '0 24px 80px', maxWidth: 780, margin: '0 auto' }}>
        <style>{`
          article p { color: var(--text); font-size: 16px; line-height: 1.85; margin-bottom: 20px; }
          article h2 { font-family: var(--font-display); font-size: 26px; font-weight: 700; letter-spacing: -0.02em; margin: 40px 0 16px; color: var(--text); }
          article h3 { font-family: var(--font-display); font-size: 20px; font-weight: 600; margin: 28px 0 12px; color: var(--text); }
          article ul, article ol { color: var(--text); font-size: 16px; line-height: 1.85; padding-left: 24px; margin-bottom: 20px; }
          article li { margin-bottom: 8px; }
          article strong { color: var(--text); font-weight: 600; }
          article em { color: var(--text2); }
          article code { font-family: var(--font-mono); font-size: 13px; background: var(--surface2); color: var(--cyan); padding: 2px 7px; border-radius: 5px; }
          article pre { background: var(--bg3); border: 1px solid var(--border); border-radius: 12px; padding: 24px; overflow-x: auto; margin: 24px 0; }
          article pre code { background: none; color: var(--text2); padding: 0; font-size: 13px; line-height: 1.7; }
          article table { border-collapse: collapse; width: 100%; margin: 8px 0 24px; font-family: var(--font-mono); font-size: 13.5px; }
          article th, article td { border: 1px solid var(--border); padding: 8px 12px; text-align: left; }
          article th { color: var(--text2); background: var(--surface); font-weight: 500; }
          article td { color: var(--text); }
        `}</style>
        {content}

        {/* Series navigation — keeps each note a single focused topic while making the basic-to-depth path obvious */}
        {otherInSeries.length > 0 && (
          <div style={{ marginTop: 64 }}>
            <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text3)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 24 }}>More in {note.series}</p>
            <div style={{ display: 'grid', gap: 12 }}>
              {otherInSeries.map(n => (
                <Link to={`/notes/${n.slug}`} key={n.slug} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
                  padding: '16px 20px', background: 'var(--surface)', border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)', transition: 'border 0.2s',
                }}
                onMouseOver={e => (e.currentTarget as HTMLElement).style.borderColor = 'var(--border2)'}
                onMouseOut={e => (e.currentTarget as HTMLElement).style.borderColor = 'var(--border)'}>
                  <div>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: n.color, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Part {n.order}</span>
                    <p style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 15, marginTop: 4 }}>{n.title}</p>
                  </div>
                  <ArrowRight size={14} style={{ color: 'var(--text3)', flexShrink: 0 }} />
                </Link>
              ))}
            </div>
          </div>
        )}

        <div style={{
          marginTop: 40, padding: 28, background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', display: 'flex', gap: 20, alignItems: 'flex-start',
        }}>
          <div style={{
            width: 52, height: 52, borderRadius: 14, flexShrink: 0,
            background: 'linear-gradient(135deg, var(--cyan), var(--violet))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 20, color: '#080b10',
          }}>P</div>
          <div>
            <p style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Parmeshwar</p>
            <p style={{ color: 'var(--text2)', fontSize: 14, lineHeight: 1.6 }}>
              Writing these up as I learn — expect basics-to-depth notes on one piece of tech at a time.
            </p>
          </div>
        </div>
      </article>
    </div>
  );
}
